import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FolderOpen, Pencil, RefreshCw } from 'lucide-react';
import { toast } from '../../lib/safeToast';

export default function BackOfficeConfigDocs() {
  const { api } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/doc-requirements');
      setDocs(res.data?.data || []);
    } catch { toast.error('Failed to load requirements'); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openEdit = (doc) => {
    setEditDoc(doc);
    setEditForm({ name: doc.name, description: doc.description, required: doc.required, applies_to: doc.applies_to });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/backoffice/doc-requirements/${editDoc.id}`, editForm);
      toast.success('Document requirement updated');
      setEditDoc(null);
      fetchDocs();
    } catch { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><FolderOpen className="w-7 h-7 text-[#0B6445]" />Document Requirements</h1>
            <p className="text-[#0E1B16]/70 mt-2">KYC / KYB document types required per business category.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDocs} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : (
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base text-[#073B2E]">{docs.length} Document Type(s)</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#DCE4E0] bg-[#F8FAF9]">
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Document</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Description</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#073B2E]">Required</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#073B2E]">Applies To</th>
                    <th className="text-right py-3 px-4 font-semibold text-[#073B2E]">Action</th>
                  </tr></thead>
                  <tbody>
                    {docs.map((d) => (
                      <tr key={d.id} className="border-b border-[#DCE4E0]/60 hover:bg-[#F8FAFC]">
                        <td className="py-3 px-4 font-medium">{d.name}</td>
                        <td className="py-3 px-4 text-slate-600 max-w-[250px]">{d.description}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline" className={d.required ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-50 text-slate-500 border-slate-200'}>{d.required ? 'Required' : 'Optional'}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center capitalize">{d.applies_to}</td>
                        <td className="py-3 px-4 text-right">
                          <Button size="sm" variant="outline" onClick={() => openEdit(d)} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={!!editDoc} onOpenChange={() => setEditDoc(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="text-[#0F172A]">Edit Document Requirement</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea value={editForm.description || ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Applies To</Label>
                <Select value={editForm.applies_to || 'all'} onValueChange={(v) => setEditForm({ ...editForm, applies_to: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="business">Business</SelectItem><SelectItem value="individual">Individual</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Label className="mb-0">Required</Label>
                <button type="button" onClick={() => setEditForm({ ...editForm, required: !editForm.required })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.required ? 'bg-[#0B6445]' : 'bg-slate-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.required ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditDoc(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0B6445] hover:bg-[#095b3d] text-white">{saving ? 'Saving…' : 'Save Changes'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BackOfficeLayout>
  );
}
